import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { translate, TranslocoService } from '@jsverse/transloco';
import { MyDataService } from 'app/modules/admin/team/my-data/my-data.service';
import { UserService } from '../services/user.service';
import { HyperlinksService } from '../services/hyperlinks.service';

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    openedMenus: boolean[] = Array(13).fill(true);

    langChangeSub: Subscription;

    constructor(
        private _httpClient: HttpClient,
        private permissionService: PermissionService,
        private translocoService: TranslocoService,
        private myDataService: MyDataService,
        private userService: UserService,
        private hyperlinksService: HyperlinksService
    ) {
        this.init();
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    async init() {
        if (!this.langChangeSub) {
            this.langChangeSub = this.translocoService.langChanges$.subscribe(
                lang => {
                    this.get();
                }
            );
        }
    }

    get(): Observable<Navigation> {
        let navigation: Navigation = {
            default: [
                {
                    number: 0,
                    id: 'hippo-data',
                    title: translate('group.hippodata'),
                    type: 'group',
                    permissions: ['news_feed_read'],
                    children: [
                        {
                            id: 'newsfeed',
                            title: translate('p_nfd'),
                            type: 'basic',
                            icon: 'heroicons_outline:newspaper',
                            link: '/newsfeed',
                            permissions: ['news_feed_read'],
                        },
                    ],
                },
                {
                    number: 1,
                    id: 'events',
                    title: translate('group.events'),
                    type: 'group',
                    permissions: [
                        'event_read',
                        'event_entertainment_expenses',
                        'project_management_read',
                        'staff_planning_job_read_active',
                        'staff_planning_vehicles_read_active',
                        'food_allowance_read_own',
                        'staff_planning_travel_manager',
                        'staff_planning_a1_read',
                        'competition_scheduling',
                        'video_screen_overview_read',
                        'competition_rating',
                    ],

                    children: [
                        {
                            id: 'events.staff-plan',
                            title: translate('p_stp'),
                            type: 'basic',
                            icon: 'heroicons_outline:users',
                            link: '/events/staff-plan',
                            permissions: [
                                'event_read',
                                'event_entertainment_expenses',
                                'project_management_read',
                                'staff_planning_job_read_active',
                                'staff_planning_vehicles_read_active',
                            ],
                        },
                        {
                            id: 'events.food-allowance',
                            title: translate('p_fau'),
                            type: 'basic',
                            icon: 'heroicons_outline:user-plus',
                            link: '/events/food-allowance',
                            permissions: ['food_allowance_read_own'],
                        },
                        {
                            id: 'events.work-plan-overview',
                            title: translate('p_wpo'),
                            type: 'basic',
                            icon: 'heroicons_outline:briefcase',
                            link: '/events/work-plan-overview',
                            permissions: [
                                '!work_plan_overview_show_self',
                                'project_management_read',
                                'staff_planning_job_read_active',
                                'staff_planning_vehicles_read_active',
                            ],
                        },
                        {
                            id: 'events.travel-plan-overview',
                            title: translate('p_tpo'),
                            type: 'basic',
                            icon: 'heroicons_outline:globe-americas',
                            link: '/events/travel-plan-overview',
                            permissions: [
                                'staff_planning_travel_manager',
                                'staff_planning_travel_data_read_all',
                            ],
                        },
                        {
                            id: 'events.certification-a1',
                            title: translate('p_sca'),
                            type: 'basic',
                            icon: 'mat_outline:assignment_turned_in',
                            link: '/events/certification-a1',
                            permissions: ['staff_planning_a1_read'],
                        },
                        {
                            id: 'events.event-progress',
                            title: translate('p_esl'),
                            type: 'basic',
                            icon: 'heroicons_outline:rectangle-group',
                            link: '/events/event-progress',
                            permissions: ['competition_scheduling'],
                        },
                        {
                            id: 'events.video-wall-overview',
                            title: translate('p_vso'),
                            type: 'basic',
                            icon: 'mat_outline:personal_video',
                            link: '/events/video-wall-overview',
                            permissions: ['video_screen_overview_read'],
                        },
                        {
                            id: 'events.event-rating-overview',
                            title: translate('p_ero'),
                            type: 'basic',
                            icon: 'heroicons_outline:star',
                            link: '/events/event-rating-overview',
                            permissions: ['competition_rating'],
                        },
                    ],
                },
                {
                    number: 2,
                    id: 'event-data',
                    title: translate('group.event_data'),
                    type: 'group',
                    permissions: ['event_data_read_all'],
                    children: [
                        {
                            id: 'event-data.organizer',
                            title: translate('p_ore'),
                            type: 'basic',
                            icon: 'heroicons_outline:building-office-2',
                            link: '/event-data/organizer',
                            permissions: ['event_data_read_all'],
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'event-data.venues',
                            title: translate('p_vno'),
                            type: 'basic',
                            icon: 'mat_outline:location_on',
                            link: '/event-data/venues',
                            permissions: ['event_data_read_all'],
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'event-data.contact-persons',
                            title: translate('p_cpo'),
                            type: 'basic',
                            icon: 'heroicons_outline:user-circle',
                            link: '/event-data/contact-persons',
                            permissions: ['event_data_read_all'],
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'event-data.contact-functions',
                            title: translate('p_cfo'),
                            type: 'basic',
                            icon: 'mat_outline:contacts',
                            link: '/event-data/contact-functions',
                            permissions: ['event_data_read_all'],
                            queryParams: { oldSystem: false },
                        },
                    ],
                },
                {
                    number: 3,
                    id: 'statistics',
                    title: translate('group.statistics'),
                    type: 'group',
                    permissions: ['show_statistics'],
                    children: [
                        {
                            id: 'statistics.event-overview',
                            title: translate('p_evo'),
                            type: 'basic',
                            icon: 'mat_outline:home_work',
                            link: '/statistics/event-overview',
                            permissions: ['show_statistics'],
                        },
                        {
                            id: 'statistics.work-overview',
                            title: translate('p_seo'),
                            type: 'basic',
                            icon: 'mat_outline:work_outline',
                            link: '/statistics/work-overview',
                            permissions: ['show_statistics'],
                        },
                        {
                            id: 'statistics.classification-conflicts',
                            title: translate('p_ssc'),
                            type: 'basic',
                            icon: 'heroicons_outline:hand-raised',
                            link: '/statistics/classification-conflicts',
                            permissions: ['show_statistics'],
                        },
                    ],
                },
                {
                    number: 4,
                    id: 'performance',
                    title: translate('group.skills'),
                    type: 'group',
                    permissions: ['skill_read'],
                    children: [
                        {
                            id: 'performance.skills',
                            title: translate('p_sks'),
                            type: 'basic',
                            icon: 'heroicons_outline:sparkles',
                            link: '/performance/skills',
                            permissions: ['skill_admin'],
                        },
                        {
                            id: 'performance.your-skills',
                            title: translate('p_ske'),
                            type: 'basic',
                            icon: 'mat_outline:supervised_user_circle',
                            link: '/performance/your-skills',
                            permissions: ['skill_read'],
                        },
                        {
                            id: 'performance.your-training-orders',
                            title: translate('p_ets'),
                            type: 'basic',
                            icon: 'mat_outline:fitness_center',
                            link: '/performance/your-training-orders',
                            permissions: ['skill_read'],
                        },
                    ],
                },
                {
                    number: 5,
                    id: 'material-management',
                    title: translate('group.material_management'),
                    type: 'group',
                    children: [
                        {
                            id: 'material-management.product-groups',
                            title: translate('p_pgo'),
                            type: 'basic',
                            icon: 'mat_outline:account_tree',
                            link: '/material-management/product-groups',
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'material-management.damage-report',
                            title: translate('p_msr'),
                            type: 'basic',
                            icon: 'mat_outline:report',
                            link: '/material-management/damage-report',
                            permissions: ['material_service_request'],
                        },
                    ],
                },
                {
                    number: 6,
                    id: 'fleet',
                    title: translate('group.fleet'),
                    type: 'group',
                    permissions: ['vehicles_read', 'route_planning_read'],
                    children: [
                        {
                            id: 'fleet.vehicles',
                            title: translate('p_vho'),
                            type: 'basic',
                            icon: 'mat_outline:directions_car',
                            link: '/fleet/vehicles',
                            permissions: ['vehicles_read'],
                        },
                        {
                            id: 'fleet.tour-overview',
                            title: translate('p_rpo'),
                            type: 'basic',
                            icon: 'mat_outline:car_repair',
                            link: '/fleet/tour-overview',
                            permissions: ['route_planning_read'],
                        },
                        {
                            id: 'fleet.vehicle-utilization',
                            title: translate('p_vhu'),
                            type: 'basic',
                            icon: 'mat_outline:car_rental',
                            link: '/fleet/vehicle-utilization',
                            permissions: ['vehicles_read'],
                        },
                    ],
                },
                {
                    number: 8,
                    id: 'software',
                    title: translate('group.software'),
                    type: 'group',
                    permissions: [
                        'dressage_tests_read',
                        'currency_converter_use',
                    ],
                    children: [
                        /*{
                            id: 'software.bug-report',
                            title: translate('p_#bug'),
                            type: 'basic',
                            icon: 'mat_outline:bug_report',
                            link: '/software/bug-report',
                        },*/
                        {
                            id: 'software.dressage-tasks',
                            title: translate('p_dto'),
                            type: 'basic',
                            icon: 'mat_outline:task',
                            link: '/software/dressage-tasks',
                            permissions: ['dressage_tests_read'],
                        },
                        {
                            id: 'software.currency-converter',
                            title: translate('p_crc'),
                            type: 'basic',
                            icon: 'mat_outline:attach_money',
                            link: '/software/currency-converter',
                            permissions: ['currency_converter_use'],
                        },
                    ],
                },
                {
                    number: 9,
                    id: 'time-tracking',
                    title: translate('p_stt'),
                    type: 'group',
                    permissions: [
                        'work_time_edit',
                        'user_holiday_planning_read_own',
                    ],
                    children: [
                        {
                            id: 'time-tracking.working-time-recording',
                            title: translate('p_utr'),
                            type: 'basic',
                            icon: 'mat_outline:timer',
                            link: '/time-tracking/working-time-recording',
                            permissions: ['work_time_edit'],
                        },
                        {
                            id: 'time-tracking.vacation-absence',
                            title: translate('p_uhy'),
                            type: 'basic',
                            icon: 'mat_outline:beach_access',
                            link: '/time-tracking/vacation-absence',
                            permissions: ['user_holiday_planning_read_own'],
                        },
                    ],
                },
                {
                    number: 10,
                    id: 'export',
                    title: translate('group.export'),
                    type: 'group',
                    permissions: ['payroll_accounting_export'],
                    children: [
                        {
                            id: 'export.payroll',
                            title: translate('p_pae'),
                            type: 'basic',
                            icon: 'mat_outline:money',
                            link: '/export/payroll',
                            permissions: ['payroll_accounting_export'],
                        },
                    ],
                },
                {
                    number: 11,
                    id: 'team',
                    title: translate('group.users'),
                    type: 'group',
                    children: [
                        {
                            id: 'team.team',
                            title: translate('p_usl'),
                            type: 'basic',
                            icon: 'heroicons_outline:user-group',
                            link: '/team/team',
                            permissions: ['user_list_read'],
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'team.my-data',
                            title: translate('p_usd'),
                            type: 'basic',
                            icon: 'heroicons_outline:rectangle-stack',
                            link: '/team/my-data',
                            queryParams: { oldSystem: false },
                        },
                        {
                            id: 'team.visa-work-permit',
                            title: translate('p_uvp'),
                            type: 'basic',
                            icon: 'mat_outline:work_outline',
                            link: '/team/visa-work-permit',
                            permissions: ['user_visa_read'],
                        },
                        {
                            id: 'team.hippo-points',
                            title: translate('p_upr'),
                            type: 'basic',
                            icon: 'heroicons_outline:hand-thumb-up',
                            link: '/team/hippo-points',
                            permissions: ['user_hippo_points_read_own'],
                        },
                    ],
                },
                {
                    number: 12,
                    id: 'system',
                    title: translate('group.system'),
                    type: 'group',
                    permissions: [
                        'hyperlinks_global_read',
                        'translation_translator',
                        'system_login_reset',
                        'e_mail_dispatch',
                    ],
                    children: [
                        {
                            id: 'system.email-dispatch',
                            title: translate('p_eds'),
                            type: 'basic',
                            icon: 'mat_outline:mark_email_read',
                            link: '/system/email-dispatch',
                            permissions: ['e_mail_dispatch'],
                        },
                        {
                            id: 'system.global-hyperlinks',
                            title: translate('p_hlk'),
                            type: 'basic',
                            icon: 'mat_outline:link',
                            link: '/system/global-hyperlinks',
                            permissions: ['hyperlinks_global_read'],
                        },
                        {
                            id: 'system.login-reset',
                            title: translate('p_lgr'),
                            type: 'basic',
                            icon: 'mat_outline:undo',
                            link: '/system/login-reset',
                            permissions: ['system_login_reset'],
                        },
                        {
                            id: 'system.system-translations',
                            title: translate('p_stl'),
                            type: 'basic',
                            icon: 'mat_outline:translate',
                            link: '/system/system-translations',
                            permissions: ['translation_translator'],
                        },
                    ],
                },
            ],
        };
        if (this.userService.user.systemAccess) {
            navigation.default[11].children.push({
                id: 'system.system-settings',
                title: 'System settings',
                type: 'basic',
                icon: 'mat_outline:settings',
                link: '/system/system-settings',
            });
        }

        let hyperlinks: any[] = [];
        if (this.hyperlinksService.hyperlinks) {
            this.hyperlinksService.hyperlinks.forEach(hyperlink => {
                hyperlinks.push({
                    id: hyperlink.hlk_id,
                    title: hyperlink.hlk_name,
                    type: 'basic',
                    icon: 'mat_outline:link',
                    link: hyperlink.hlk_link,
                    externalLink: true,
                    target: '_blank',
                });
            });
            if (hyperlinks.length) {
                navigation.default.splice(7, 0, {
                    number: 7,
                    id: 'hyperlinks',
                    title: 'Hyperlinks',
                    type: 'group',
                    children: hyperlinks,
                });
            }
        }
        /*if(environment.isTestSystem) {
            navigation.default[12].children.push({
                id: 'system.system-settings',
                title: 'System settings',
                type: 'basic',
                icon: 'mat_outline:settings',
                link: '/system/system-settings',

            })
        }*/

        this._navigation.next(navigation);
        return of(navigation);
    }

    ngOnDestroy(): void {
        if (this.langChangeSub) {
            this.langChangeSub.unsubscribe();
        }
    }
}
